import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import useDocumentTitle from "@/hooks/useDocumentTitle";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import {
  Plus, Trash, ListChecks, Broom, PencilSimple, FloppyDisk, Briefcase,
  CheckCircle, ArrowClockwise, Eye, EyeSlash, User, CurrencyEur, ClipboardText, Note, Receipt, ThumbsUp,
} from "@phosphor-icons/react";
import { toast } from "sonner";

const PRIORITY = {
  low: { label: "Bassa", class: "bg-blue-500/15 text-blue-700 dark:text-blue-400 border-blue-500/30" },
  normal: { label: "Normale", class: "bg-secondary text-foreground border-border" },
  high: { label: "Alta", class: "bg-rose-500/15 text-rose-700 dark:text-rose-400 border-rose-500/30" },
};

const STAGE_META = {
  draft: { label: "Bozza", class: "bg-secondary text-muted-foreground border-border" },
  preventivo: { label: "Preventivo", class: "bg-amber-500/15 text-amber-700 dark:text-amber-400 border-amber-500/40" },
  accettato: { label: "Accettato", class: "bg-emerald-500/15 text-emerald-700 dark:text-emerald-400 border-emerald-500/40" },
};

const eur = (n) => (n ?? 0).toLocaleString("it-IT", { style: "currency", currency: "EUR", maximumFractionDigits: 2 });
const fmtDate = (s) => s ? new Date(s).toLocaleString("it-IT", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "—";

export default function TasksPage() {
  const [sp, setSp] = useSearchParams();
  const tab = sp.get("tab") === "tasks" ? "tasks" : "works";

  useDocumentTitle(tab === "tasks" ? "Task" : "Lavori");

  return (
    <div className="p-6 lg:p-10 max-w-5xl mx-auto" data-testid="lavori-page">
      <header className="mb-6 flex flex-col sm:flex-row sm:items-end justify-between gap-4">
        <div>
          <div className="font-mono text-xs uppercase tracking-widest text-muted-foreground mb-2">// GESTIONE</div>
          <h1 className="text-4xl font-display font-bold tracking-tight flex items-center gap-3">
            {tab === "tasks" ? (
              <>
                <ListChecks size={36} weight="duotone" className="text-primary" />
                Task
              </>
            ) : (
              <>
                <Briefcase size={36} weight="duotone" className="text-primary" />
                Lavori
              </>
            )}
          </h1>
          <p className="text-muted-foreground mt-2">
            {tab === "tasks" ? "Cose da fare rapide, senza cliente." : "Lavori con cliente, preventivo, accettazione e chiusura."}
          </p>
        </div>
      </header>

      {tab === "works" ? <WorksSection /> : <TasksSection />}
    </div>
  );
}

/* ================== TASKS SECTION ================== */

function TasksSection() {
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [title, setTitle] = useState("");
  const [priority, setPriority] = useState("normal");
  const [creating, setCreating] = useState(false);
  const [showClosed, setShowClosed] = useState(false);
  const [priorityFilter, setPriorityFilter] = useState("all");
  const [editTask, setEditTask] = useState({ open: false, task: null, title: "", priority: "normal" });

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/tasks");
      setTasks(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const create = async (e) => {
    e.preventDefault();
    if (!title.trim()) return;
    setCreating(true);
    try {
      await api.post("/tasks", { title: title.trim(), priority });
      setTitle(""); setPriority("normal");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setCreating(false); }
  };

  const toggle = async (t) => {
    setTasks((cur) => cur.map((x) => x.id === t.id ? { ...x, done: !x.done } : x));
    try { await api.patch(`/tasks/${t.id}`, { done: !t.done }); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); load(); }
  };

  const remove = async (t) => {
    if (!window.confirm("Eliminare la task?")) return;
    try { await api.delete(`/tasks/${t.id}`); setTasks((cur) => cur.filter((x) => x.id !== t.id)); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const clearDone = async () => {
    if (!window.confirm("Eliminare tutte le task completate?")) return;
    try {
      const { data } = await api.delete("/tasks/all-done");
      toast.success(`${data.deleted} task rimosse`);
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const openEdit = (t) => setEditTask({ open: true, task: t, title: t.title, priority: t.priority });

  const saveEdit = async () => {
    const t = editTask.task;
    if (!editTask.title.trim()) { toast.error("Titolo richiesto"); return; }
    try {
      await api.patch(`/tasks/${t.id}`, { title: editTask.title.trim(), priority: editTask.priority });
      toast.success("Task aggiornata");
      setEditTask({ open: false, task: null, title: "", priority: "normal" });
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const priorityRank = { high: 0, normal: 1, low: 2 };
  const visible = useMemo(() => tasks
    .filter((t) => showClosed ? true : !t.done)
    .filter((t) => (priorityFilter === "all" ? true : t.priority === priorityFilter))
    .slice()
    .sort((a, b) => {
      if (a.done !== b.done) return a.done ? 1 : -1;
      const pa = priorityRank[a.priority] ?? 1; const pb = priorityRank[b.priority] ?? 1;
      if (pa !== pb) return pa - pb;
      return new Date(b.created_at) - new Date(a.created_at);
    }), [tasks, showClosed, priorityFilter]);

  const openCount = tasks.filter((t) => !t.done).length;
  const doneCount = tasks.filter((t) => t.done).length;

  return (
    <div>
      <Card className="p-4 border-border/70 rounded-lg mb-4">
        <form onSubmit={create} className="flex flex-col sm:flex-row gap-2" data-testid="new-task-form">
          <Input
            placeholder="Cosa devi fare?  (es. Ordinare SSD, chiamare fornitore...)"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            data-testid="new-task-title"
            className="flex-1"
          />
          <Select value={priority} onValueChange={setPriority}>
            <SelectTrigger className="w-40" data-testid="new-task-priority"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="low">Priorità bassa</SelectItem>
              <SelectItem value="normal">Priorità normale</SelectItem>
              <SelectItem value="high">Priorità alta</SelectItem>
            </SelectContent>
          </Select>
          <Button type="submit" disabled={creating || !title.trim()} data-testid="new-task-submit" className="rounded-full gap-2">
            <Plus size={16} weight="bold" /> Aggiungi task
          </Button>
        </form>
      </Card>

      <div className="flex flex-wrap items-center gap-3 mb-4">
        <div className="font-mono text-xs text-muted-foreground">
          {openCount} da fare · {doneCount} completate
        </div>
        <div className="ml-auto flex flex-wrap gap-2">
          <Select value={priorityFilter} onValueChange={setPriorityFilter}>
            <SelectTrigger className="w-40" data-testid="task-priority-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tutte priorità</SelectItem>
              <SelectItem value="high">Solo Alta</SelectItem>
              <SelectItem value="normal">Solo Normale</SelectItem>
              <SelectItem value="low">Solo Bassa</SelectItem>
            </SelectContent>
          </Select>
          <Button
            variant={showClosed ? "default" : "outline"}
            onClick={() => setShowClosed(!showClosed)}
            data-testid="task-toggle-closed"
            className="rounded-full gap-2"
          >
            {showClosed ? <EyeSlash size={14} /> : <Eye size={14} />}
            {showClosed ? `Nascondi chiuse (${doneCount})` : `Mostra chiuse (${doneCount})`}
          </Button>
          {doneCount > 0 && (
            <Button variant="outline" onClick={clearDone} data-testid="clear-done-btn" className="rounded-full gap-2">
              <Broom size={14} /> Pulisci
            </Button>
          )}
        </div>
      </div>

      <Card className="border-border/70 rounded-lg overflow-hidden">
        {loading && <div className="p-8 text-center text-muted-foreground">Caricamento…</div>}
        {!loading && visible.length === 0 && (
          <div className="p-12 text-center">
            <ListChecks size={40} weight="duotone" className="mx-auto text-muted-foreground mb-3" />
            <div className="text-muted-foreground">Nessuna task {showClosed ? "" : "aperta"}.</div>
          </div>
        )}
        {!loading && visible.length > 0 && (
          <ul className="divide-y divide-border/50" data-testid="task-list">
            {visible.map((t) => (
              <li key={t.id} data-testid={`task-item-${t.id}`}
                  className={`flex items-center gap-3 px-4 py-3 hover:bg-secondary/40 ${t.done ? "bg-secondary/20" : ""}`}
                  style={{ transition: "background-color 0.15s ease" }}>
                <Checkbox
                  checked={t.done}
                  onCheckedChange={() => toggle(t)}
                  data-testid={`task-check-${t.id}`}
                  className="shrink-0"
                />
                <div className="flex-1 min-w-0">
                  <div className={`${t.done ? "line-through text-muted-foreground" : "font-medium"} truncate`}>{t.title}</div>
                  <div className="flex items-center gap-2 mt-1 flex-wrap">
                    <Badge className={`font-mono text-[9px] uppercase border ${PRIORITY[t.priority]?.class || PRIORITY.normal.class} hover:opacity-100`}>
                      {PRIORITY[t.priority]?.label || t.priority}
                    </Badge>
                    <span className="font-mono text-[10px] text-muted-foreground">Creata {fmtDate(t.created_at)}</span>
                    {t.done && t.done_at && (
                      <span className="font-mono text-[10px] text-emerald-600 dark:text-emerald-400">Chiusa {fmtDate(t.done_at)}</span>
                    )}
                  </div>
                </div>
                {!t.done && (
                  <Button variant="ghost" size="sm" onClick={() => openEdit(t)} data-testid={`task-edit-${t.id}`}
                          className="shrink-0" title="Modifica">
                    <PencilSimple size={14} />
                  </Button>
                )}
                <Button variant="ghost" size="sm" onClick={() => remove(t)} data-testid={`task-delete-${t.id}`}
                        className="text-destructive hover:text-destructive shrink-0">
                  <Trash size={14} />
                </Button>
              </li>
            ))}
          </ul>
        )}
      </Card>

      {/* Edit task dialog */}
      <Dialog open={editTask.open} onOpenChange={(o) => !o && setEditTask({ open: false, task: null, title: "", priority: "normal" })}>
        <DialogContent data-testid="edit-task-dialog">
          <DialogHeader>
            <DialogTitle>Modifica task</DialogTitle>
            <DialogDescription>Aggiorna testo e priorità.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="font-mono text-[10px] uppercase">Testo</Label>
              <Input value={editTask.title} onChange={(e) => setEditTask({ ...editTask, title: e.target.value })} data-testid="edit-task-title" />
            </div>
            <div>
              <Label className="font-mono text-[10px] uppercase">Priorità</Label>
              <Select value={editTask.priority} onValueChange={(v) => setEditTask({ ...editTask, priority: v })}>
                <SelectTrigger data-testid="edit-task-priority"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="low">Bassa</SelectItem>
                  <SelectItem value="normal">Normale</SelectItem>
                  <SelectItem value="high">Alta</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setEditTask({ open: false, task: null, title: "", priority: "normal" })}>Annulla</Button>
            <Button onClick={saveEdit} data-testid="edit-task-save" className="rounded-full gap-2">
              <FloppyDisk size={14} weight="bold" /> Salva
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

/* ================== WORKS SECTION ================== */

function WorksSection() {
  const [works, setWorks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showClosed, setShowClosed] = useState(false);
  const [newDialog, setNewDialog] = useState({ open: false });
  const [editDialog, setEditDialog] = useState({ open: false, work: null, form: {} });
  const [savingEdit, setSavingEdit] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/works");
      setWorks(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const openWorks = works.filter((w) => w.status === "open");
  const closedWorks = works.filter((w) => w.status === "closed");
  const visible = showClosed ? closedWorks : openWorks;

  const setStage = async (w, stage) => {
    try {
      const { data } = await api.patch(`/works/${w.id}`, { stage });
      setWorks((cur) => cur.map((x) => x.id === data.id ? data : x));
      toast.success(stage === "preventivo" ? "Preventivo segnato" : stage === "accettato" ? "Lavoro accettato" : "Aggiornato");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const closeWork = async (w) => {
    if (w.stage !== "accettato") {
      toast.error("Devi prima segnare 'Preventivo' e 'Accettato' prima di chiudere");
      return;
    }
    if (!window.confirm(`Chiudere il lavoro?\n\nSarà aggiunto ai movimenti dashboard sotto "Lavori" per ${eur(w.cost)}.`)) return;
    try {
      const { data } = await api.post(`/works/${w.id}/close`);
      setWorks((cur) => cur.map((x) => x.id === data.id ? data : x));
      toast.success("Lavoro chiuso — movimento registrato in dashboard");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const reopenWork = async (w) => {
    try {
      const { data } = await api.post(`/works/${w.id}/reopen`);
      setWorks((cur) => cur.map((x) => x.id === data.id ? data : x));
      toast.success("Lavoro riaperto");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const removeWork = async (w) => {
    if (!window.confirm(`Eliminare il lavoro "${w.subject}"?`)) return;
    try {
      await api.delete(`/works/${w.id}`);
      setWorks((cur) => cur.filter((x) => x.id !== w.id));
      toast.success("Lavoro eliminato");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const openEdit = (w) => setEditDialog({ open: true, work: w, form: { subject: w.subject, description: w.description || "", cost: w.cost } });

  const saveEdit = async () => {
    const w = editDialog.work;
    const cost = parseFloat(editDialog.form.cost);
    if (!editDialog.form.subject.trim()) { toast.error("Oggetto richiesto"); return; }
    if (isNaN(cost) || cost < 0) { toast.error("Costo non valido"); return; }
    setSavingEdit(true);
    try {
      const { data } = await api.patch(`/works/${w.id}`, {
        subject: editDialog.form.subject.trim(),
        description: editDialog.form.description || "",
        cost,
      });
      setWorks((cur) => cur.map((x) => x.id === data.id ? data : x));
      setEditDialog({ open: false, work: null, form: {} });
      toast.success("Lavoro aggiornato");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setSavingEdit(false); }
  };

  return (
    <div>
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <div className="font-mono text-xs text-muted-foreground">
          {openWorks.length} aperti · {closedWorks.length} chiusi
        </div>
        <div className="ml-auto flex gap-2 flex-wrap">
          <Button
            variant={showClosed ? "default" : "outline"}
            onClick={() => setShowClosed(!showClosed)}
            data-testid="work-toggle-closed"
            className="rounded-full gap-2"
          >
            {showClosed ? <EyeSlash size={14} /> : <Eye size={14} />}
            {showClosed ? `Nascondi chiusi (${closedWorks.length})` : `Mostra chiusi (${closedWorks.length})`}
          </Button>
          <Button onClick={() => setNewDialog({ open: true })} data-testid="new-work-btn" className="rounded-full gap-2">
            <Plus size={16} weight="bold" /> Nuovo lavoro
          </Button>
        </div>
      </div>

      {loading && <div className="text-muted-foreground">Caricamento…</div>}
      {!loading && visible.length === 0 && (
        <Card className="p-12 text-center border-border/70 border-dashed rounded-lg">
          <Briefcase size={40} weight="duotone" className="mx-auto text-muted-foreground mb-3" />
          <div className="text-muted-foreground font-mono text-sm">— Nessun lavoro {showClosed ? "chiuso" : "aperto"} —</div>
        </Card>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {visible.map((w) => {
          const sm = STAGE_META[w.stage] || STAGE_META.draft;
          const isClosed = w.status === "closed";
          return (
            <Card key={w.id} className="p-5 border border-border/70 rounded-lg" data-testid={`work-${w.id}`}>
              <div className="flex items-start justify-between gap-3 mb-2">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-1.5 flex-wrap mb-2">
                    <Badge className={`${sm.class} border font-mono text-[9px] uppercase`}>{sm.label}</Badge>
                    {isClosed && (
                      <Badge className="bg-emerald-500/15 text-emerald-700 dark:text-emerald-400 border-emerald-500/40 border font-mono text-[9px] uppercase">Chiuso</Badge>
                    )}
                    {w.cost > 0 && (
                      <Badge className="bg-secondary text-foreground border-border font-mono text-[10px]">
                        <CurrencyEur size={10} weight="fill" /> {eur(w.cost)}
                      </Badge>
                    )}
                  </div>
                  <div className="font-semibold flex items-center gap-1.5">
                    <User size={14} className="text-muted-foreground" />
                    {w.customer_surname} {w.customer_name}
                  </div>
                  <div className="text-sm mt-1 font-medium">{w.subject}</div>
                </div>
              </div>

              {w.description && (
                <div className="text-sm bg-secondary/40 border border-border/50 rounded-md p-3 mb-3 max-h-32 overflow-auto whitespace-pre-wrap font-mono text-xs">
                  {w.description}
                </div>
              )}

              <div className="font-mono text-[10px] text-muted-foreground mb-3 space-y-0.5">
                <div>Creato: {fmtDate(w.created_at)}</div>
                {w.closed_at && <div>Chiuso: {fmtDate(w.closed_at)}</div>}
              </div>

              {!isClosed && (
                <div className="flex items-center gap-1.5 flex-wrap">
                  <Button
                    size="sm"
                    variant={w.stage === "draft" ? "outline" : "default"}
                    onClick={() => setStage(w, w.stage === "preventivo" || w.stage === "accettato" ? "draft" : "preventivo")}
                    disabled={w.stage === "accettato"}
                    data-testid={`work-preventivo-${w.id}`}
                    className={`h-7 rounded-full gap-1 text-xs ${w.stage === "preventivo" || w.stage === "accettato" ? "bg-amber-500 hover:bg-amber-600 text-white" : ""}`}
                    title={w.stage === "accettato" ? "Non modificabile: già accettato" : "Segna come preventivo inviato"}
                  >
                    <Receipt size={12} weight={w.stage === "preventivo" || w.stage === "accettato" ? "fill" : "regular"} />
                    Preventivo
                  </Button>
                  <Button
                    size="sm"
                    variant={w.stage === "accettato" ? "default" : "outline"}
                    onClick={() => setStage(w, w.stage === "accettato" ? "preventivo" : "accettato")}
                    disabled={w.stage === "draft"}
                    data-testid={`work-accettato-${w.id}`}
                    className={`h-7 rounded-full gap-1 text-xs ${w.stage === "accettato" ? "bg-emerald-600 hover:bg-emerald-700 text-white" : ""}`}
                    title={w.stage === "draft" ? "Segna prima il preventivo" : "Segna come accettato dal cliente"}
                  >
                    <ThumbsUp size={12} weight={w.stage === "accettato" ? "fill" : "regular"} />
                    Accettato
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => openEdit(w)} data-testid={`work-edit-${w.id}`} className="h-7 rounded-full gap-1 text-xs" title="Modifica">
                    <PencilSimple size={12} />
                  </Button>
                  <div className="ml-auto flex items-center gap-1">
                    <Button
                      size="sm"
                      onClick={() => closeWork(w)}
                      disabled={w.stage !== "accettato"}
                      data-testid={`work-close-${w.id}`}
                      className="h-7 rounded-full gap-1 text-xs"
                      title={w.stage !== "accettato" ? "Segna prima come accettato" : "Chiudi e registra movimento"}
                    >
                      <CheckCircle size={12} weight="bold" /> Chiudi
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => removeWork(w)} className="h-7 w-7 p-0 text-destructive hover:text-destructive hover:bg-destructive/10" data-testid={`work-delete-${w.id}`}>
                      <Trash size={13} />
                    </Button>
                  </div>
                </div>
              )}
              {isClosed && (
                <div className="flex items-center gap-1">
                  <Button size="sm" variant="outline" onClick={() => reopenWork(w)} data-testid={`work-reopen-${w.id}`} className="h-7 rounded-full gap-1 text-xs">
                    <ArrowClockwise size={12} /> Riapri
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => removeWork(w)} className="ml-auto h-7 w-7 p-0 text-destructive hover:text-destructive hover:bg-destructive/10" data-testid={`work-delete-${w.id}`}>
                    <Trash size={13} />
                  </Button>
                </div>
              )}
            </Card>
          );
        })}
      </div>

      <NewWorkDialog
        open={newDialog.open}
        onClose={() => setNewDialog({ open: false })}
        onCreated={(w) => { setWorks((cur) => [w, ...cur]); setNewDialog({ open: false }); }}
      />

      {/* Edit work dialog */}
      <Dialog open={editDialog.open} onOpenChange={(o) => !o && setEditDialog({ open: false, work: null, form: {} })}>
        <DialogContent className="max-w-lg" data-testid="edit-work-dialog">
          <DialogHeader>
            <DialogTitle>Modifica lavoro</DialogTitle>
            <DialogDescription>{editDialog.work?.customer_surname} {editDialog.work?.customer_name}</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="font-mono text-[10px] uppercase">Oggetto *</Label>
              <Input value={editDialog.form.subject || ""} onChange={(e) => setEditDialog({ ...editDialog, form: { ...editDialog.form, subject: e.target.value } })} data-testid="edit-work-subject" />
            </div>
            <div>
              <Label className="font-mono text-[10px] uppercase">Descrizione / appunti</Label>
              <Textarea rows={4} value={editDialog.form.description || ""} onChange={(e) => setEditDialog({ ...editDialog, form: { ...editDialog.form, description: e.target.value } })} data-testid="edit-work-description" />
            </div>
            <div>
              <Label className="font-mono text-[10px] uppercase">Costo (€)</Label>
              <Input type="number" step="0.01" min="0" value={editDialog.form.cost ?? ""} onChange={(e) => setEditDialog({ ...editDialog, form: { ...editDialog.form, cost: e.target.value } })} data-testid="edit-work-cost" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setEditDialog({ open: false, work: null, form: {} })}>Annulla</Button>
            <Button onClick={saveEdit} disabled={savingEdit} data-testid="edit-work-save" className="rounded-full gap-2">
              <FloppyDisk size={14} weight="bold" /> {savingEdit ? "Salvo…" : "Salva"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

/* ================== NEW WORK DIALOG (with inline customer) ================== */

function NewWorkDialog({ open, onClose, onCreated }) {
  const [step, setStep] = useState(1); // 1 = choose customer, 2 = work details
  const [customerMode, setCustomerMode] = useState("existing"); // existing | new
  const [customerId, setCustomerId] = useState(null);
  const [customerLabel, setCustomerLabel] = useState("");
  const [query, setQuery] = useState("");
  const [customers, setCustomers] = useState([]);
  const [newCustomer, setNewCustomer] = useState({ name: "", surname: "", phone: "", email: "", address: "" });
  const [form, setForm] = useState({ subject: "", description: "", cost: "" });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) {
      // reset on close
      setStep(1); setCustomerMode("existing"); setCustomerId(null); setCustomerLabel(""); setQuery("");
      setCustomers([]); setNewCustomer({ name: "", surname: "", phone: "", email: "", address: "" });
      setForm({ subject: "", description: "", cost: "" });
    }
  }, [open]);

  useEffect(() => {
    if (!open || customerMode !== "existing") return;
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get("/customers", { params: query.trim() ? { q: query.trim() } : {} });
        setCustomers(data.slice(0, 40));
      } catch { /* silent */ }
    }, 200);
    return () => clearTimeout(t);
  }, [query, open, customerMode]);

  const pickCustomer = (c) => {
    setCustomerId(c.id);
    setCustomerLabel(`${c.surname} ${c.name} · ${c.phone}`);
    setStep(2);
  };

  const createCustomerAndProceed = async () => {
    if (!newCustomer.surname.trim() || !newCustomer.name.trim() || !newCustomer.phone.trim()) {
      toast.error("Cognome, nome e telefono sono obbligatori");
      return;
    }
    try {
      const { data } = await api.post("/customers", {
        name: newCustomer.name.trim(),
        surname: newCustomer.surname.trim(),
        phone: newCustomer.phone.trim(),
        email: (newCustomer.email || "").trim() || null,
        address: (newCustomer.address || "").trim() || null,
      });
      setCustomerId(data.id);
      setCustomerLabel(`${data.surname} ${data.name} · ${data.phone}`);
      setStep(2);
      toast.success("Cliente creato");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const submit = async () => {
    if (!customerId) { toast.error("Seleziona o crea un cliente"); return; }
    if (!form.subject.trim()) { toast.error("Oggetto richiesto"); return; }
    const cost = parseFloat(form.cost || "0");
    if (isNaN(cost) || cost < 0) { toast.error("Costo non valido"); return; }
    setSaving(true);
    try {
      const { data } = await api.post("/works", {
        customer_id: customerId,
        subject: form.subject.trim(),
        description: form.description || "",
        cost,
      });
      toast.success("Lavoro creato");
      onCreated(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg" data-testid="new-work-dialog">
        <DialogHeader>
          <DialogTitle>Nuovo lavoro — {step === 1 ? "Cliente" : "Dettagli"}</DialogTitle>
          <DialogDescription>
            {step === 1 ? "Seleziona un cliente esistente o creane uno nuovo." : customerLabel}
          </DialogDescription>
        </DialogHeader>

        {step === 1 && (
          <div className="space-y-3">
            <div className="flex gap-1 bg-secondary rounded-md p-1 w-fit">
              <button onClick={() => setCustomerMode("existing")} data-testid="picker-existing"
                      className={`px-3 py-1.5 rounded text-sm ${customerMode === "existing" ? "bg-background shadow-sm" : "text-muted-foreground"}`}>
                Esistente
              </button>
              <button onClick={() => setCustomerMode("new")} data-testid="picker-new"
                      className={`px-3 py-1.5 rounded text-sm ${customerMode === "new" ? "bg-background shadow-sm" : "text-muted-foreground"}`}>
                Nuovo
              </button>
            </div>

            {customerMode === "existing" ? (
              <>
                <Input
                  placeholder="Cerca per nome, cognome, telefono…"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  data-testid="new-work-customer-search"
                  autoFocus
                />
                <div className="border border-border/60 rounded-md max-h-64 overflow-y-auto divide-y divide-border/50">
                  {customers.length === 0 && (
                    <div className="p-4 text-sm text-muted-foreground text-center">Nessun cliente trovato</div>
                  )}
                  {customers.map((c) => (
                    <button
                      type="button"
                      key={c.id}
                      onClick={() => pickCustomer(c)}
                      data-testid={`new-work-customer-${c.id}`}
                      className="w-full text-left p-3 hover:bg-secondary/60 flex justify-between items-center"
                      style={{ transition: "background-color 0.15s ease" }}
                    >
                      <div>
                        <div className="font-medium">{c.surname} {c.name}</div>
                        <div className="font-mono text-xs text-muted-foreground">{c.phone}</div>
                      </div>
                    </button>
                  ))}
                </div>
              </>
            ) : (
              <div className="grid grid-cols-2 gap-2">
                <div><Label className="font-mono text-[10px] uppercase">Cognome *</Label><Input value={newCustomer.surname} onChange={(e) => setNewCustomer({ ...newCustomer, surname: e.target.value })} data-testid="new-cust-surname" /></div>
                <div><Label className="font-mono text-[10px] uppercase">Nome *</Label><Input value={newCustomer.name} onChange={(e) => setNewCustomer({ ...newCustomer, name: e.target.value })} data-testid="new-cust-name" /></div>
                <div><Label className="font-mono text-[10px] uppercase">Telefono *</Label><Input value={newCustomer.phone} onChange={(e) => setNewCustomer({ ...newCustomer, phone: e.target.value })} data-testid="new-cust-phone" /></div>
                <div><Label className="font-mono text-[10px] uppercase">Email</Label><Input type="email" value={newCustomer.email} onChange={(e) => setNewCustomer({ ...newCustomer, email: e.target.value })} data-testid="new-cust-email" /></div>
                <div className="col-span-2"><Label className="font-mono text-[10px] uppercase">Indirizzo</Label><Input value={newCustomer.address} onChange={(e) => setNewCustomer({ ...newCustomer, address: e.target.value })} data-testid="new-cust-address" /></div>
              </div>
            )}
          </div>
        )}

        {step === 2 && (
          <div className="space-y-3">
            <div>
              <Label className="font-mono text-[10px] uppercase">Oggetto del lavoro *</Label>
              <Input
                placeholder="Es. Assemblaggio nuovo PC ufficio"
                value={form.subject}
                onChange={(e) => setForm({ ...form, subject: e.target.value })}
                data-testid="new-work-subject"
                autoFocus
              />
            </div>
            <div>
              <Label className="font-mono text-[10px] uppercase">Descrizione / appunti</Label>
              <Textarea
                rows={4}
                placeholder="Note interne, dettagli, materiale necessario…"
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                data-testid="new-work-description"
              />
            </div>
            <div>
              <Label className="font-mono text-[10px] uppercase">Costo (€)</Label>
              <Input
                type="number" step="0.01" min="0"
                placeholder="Es. 450.00"
                value={form.cost}
                onChange={(e) => setForm({ ...form, cost: e.target.value })}
                data-testid="new-work-cost"
              />
              <div className="text-[10px] text-muted-foreground font-mono mt-1">
                Il costo verrà registrato nella dashboard solo alla chiusura, se il lavoro sarà stato accettato.
              </div>
            </div>
          </div>
        )}

        <DialogFooter>
          {step === 1 && customerMode === "new" && (
            <>
              <Button variant="ghost" onClick={onClose}>Annulla</Button>
              <Button onClick={createCustomerAndProceed} data-testid="new-work-create-customer" className="rounded-full gap-2">
                <Plus size={14} weight="bold" /> Crea cliente e continua
              </Button>
            </>
          )}
          {step === 1 && customerMode === "existing" && (
            <Button variant="ghost" onClick={onClose}>Annulla</Button>
          )}
          {step === 2 && (
            <>
              <Button variant="ghost" onClick={() => setStep(1)}>Indietro</Button>
              <Button onClick={submit} disabled={saving} data-testid="new-work-submit" className="rounded-full gap-2">
                <FloppyDisk size={14} weight="bold" /> {saving ? "Creo…" : "Crea lavoro"}
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
